import { usePageTitle } from "@/hooks/usePageTitle";
import { useSearchParams, Link } from "react-router-dom";
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { LearnMoreModal } from "@/components/LearnMoreModal";

const VIRTUAL_OPEN = true;
const PLANS = [
  { id: "virtual", name: "Virtual", price: 18, tag: "Online", available: VIRTUAL_OPEN, comingLabel: "Opens 1st September", features: ["Menu ideas & recipes", "Nutrition teaching library", "Community access", "Weekly emails with deeper dives into nutrition and health plus new recipes and cookery tips"] },
  { id: "physical", name: "Physical — In person", price: 28, tag: "In-person coaching", available: false, comingLabel: "Coming early 2027", features: ["Local in-person classes", "BIA body-composition testing", "Therapist-led coaching", "Full digital content library"] },
  { id: "hybrid", name: "Hybrid", price: 38, tag: "Best of both worlds", available: false, comingLabel: "Coming early 2027", features: ["All the benefits of Virtual and Physical memberships"] },
];

export default function JoinClient() {
  usePageTitle("Membership Plans");
  const [params] = useSearchParams();
  const { user } = useAuth();
  const ref = params.get("ref") || "";
  const [selected, setSelected] = useState(VIRTUAL_OPEN ? "virtual" : "");
  const [email, setEmail] = useState("");
  const [newsletter, setNewsletter] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  const checkout = async () => {
    if (!user && !email) { toast.error("Please enter your email to continue."); return; }
    if (!terms) { toast.error("Please accept the Terms & Conditions to continue."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan: selected,
        origin_url: window.location.origin,
        referral_code: ref || undefined,
        email: user ? undefined : email,
        newsletter_opt_in: newsletter,
        terms_accepted: terms,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Join My Best Body Club</span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl tracking-tight text-navy">Pick your membership</h1>
          <p className="mt-4 text-[#57534E]">Secure checkout via Stripe. Cancel anytime.</p>
          {ref && <p className="mt-3 inline-block bg-teal/10 text-teal text-sm font-medium px-4 py-1.5 rounded-full" data-testid="referral-badge">Referred by therapist · {ref}</p>}
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {PLANS.map((p) => (
            <button key={p.id} onClick={() => p.available && setSelected(p.id)} disabled={!p.available} data-testid={`select-plan-${p.id}`}
              className={`text-left bg-white rounded-2xl p-7 border-2 transition-all ${!p.available ? "opacity-70 cursor-not-allowed" : ""} ${selected === p.id ? "border-teal shadow-xl" : "border-[#E7E5E4] hover:border-[#D6D3D1]"}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-2xl text-navy">{p.name}</h3>
                {p.available
                  ? <span className="bg-teal text-white text-xs font-semibold px-3 py-1 rounded-full">Available now</span>
                  : <span className={`text-white text-xs font-semibold px-3 py-1 rounded-full ${p.id === "virtual" ? "bg-teal" : "bg-navy"}`}>{p.comingLabel}</span>}
              </div>
              <p className="text-sm text-[#78716C]">{p.tag}</p>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-heading text-4xl text-navy">£{p.price}</span>
                <span className="text-[#78716C] mb-1">/mo</span>
              </div>
              <ul className="mt-5 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#44403C]"><Check className="w-4 h-4 text-teal mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
              {p.available && (
                <div className={`mt-5 h-5 w-5 rounded-full border-2 ${selected === p.id ? "border-teal bg-teal" : "border-[#D6D3D1]"} flex items-center justify-center`}>
                  {selected === p.id && <Check className="w-3 h-3 text-white" />}
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="mt-10 max-w-md mx-auto">
          {VIRTUAL_OPEN ? (
            <div className="bg-white border border-[#E7E5E4] rounded-2xl p-6 shadow-sm">
              {!user && (
                <div className="mb-4">
                  <label className="text-sm font-medium text-navy">Email for your membership</label>
                  <input data-testid="checkout-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                    className="mt-1.5 w-full bg-white border border-[#E7E5E4] rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" />
                  <p className="mt-2 text-xs text-[#78716C]">Already a member? <Link to="/login" className="text-teal">Log in first</Link> to link this to your account.</p>
                </div>
              )}
              <label className="mb-4 flex items-start gap-2.5 cursor-pointer">
                <input data-testid="checkout-terms" type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[#D6D3D1] accent-[#0d9488]" />
                <span className="text-xs text-[#57534E]">
                  I agree to the <Link to="/terms" target="_blank" className="text-teal font-medium underline">Terms &amp; Conditions</Link> and{" "}
                  <Link to="/privacy" target="_blank" className="text-teal font-medium underline">Privacy Policy</Link>, and I request
                  immediate access to member content — I understand this waives my 14-day cooling-off right once I access any Content.
                </span>
              </label>
              <label className="mb-4 flex items-start gap-2.5 cursor-pointer">
                <input data-testid="checkout-newsletter" type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[#D6D3D1] accent-[#0d9488]" />
                <span className="text-xs text-[#57534E]">Keep me updated with MBBC news, recipes and metabolic health tips. Unsubscribe anytime.</span>
              </label>
              <button onClick={checkout} disabled={busy || !selected || !terms} data-testid="checkout-submit"
                className="w-full bg-navy text-white hover:bg-navy-dark transition-colors rounded-full py-3.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue to payment <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          ) : (
            <div className="bg-teal/10 border border-teal/30 rounded-2xl p-7 text-center" data-testid="enrolment-notice">
              <p className="font-heading text-xl text-navy">Enrolment opens 1st September</p>
              <p className="mt-2.5 text-sm text-[#57534E] leading-relaxed">
                Memberships aren't on sale just yet — join the waitlist and we'll email you the full details
                plus first access when enrolment opens.
              </p>
              <button onClick={() => setWaitlistOpen(true)} data-testid="join-waitlist-btn"
                className="mt-5 inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark transition-colors rounded-full px-7 py-3 font-medium">
                Join the waitlist <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </section>
      <LearnMoreModal open={waitlistOpen} onClose={() => setWaitlistOpen(false)} />
      <Footer />
    </div>
  );
}
